/**
 * BudgetComments Component
 *
 * Sistema de comentários interno e externo para orçamentos
 */

import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  MessageSquare,
  Reply,
  Edit,
  Trash2,
  Send,
  Paperclip,
  AtSign,
  Hash,
  Bold,
  Italic,
  Link,
  MoreVertical,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  Info,
  Filter,
  Search,
  Plus,
  Bell,
  BellOff,
  ExternalLink,
  Download,
  Star,
  Flag,
  Eye,
  EyeOff,
  Users,
  Building,
  Lock,
  Globe
} from 'lucide-react';

interface Comment {
  id: string;
  content: string;
  author: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role?: string;
  };
  type: 'internal' | 'external' | 'note';
  visibility: 'public' | 'private' | 'team' | 'client';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'resolved' | 'closed';
  mentions?: string[];
  tags?: string[];
  attachments?: {
    id: string;
    name: string;
    url: string;
    size: string;
    type: string;
  }[];
  replies?: Comment[];
  createdAt: string;
  updatedAt: string;
  resolvedBy?: {
    id: string;
    name: string;
  };
  resolvedAt?: string;
}

interface BudgetCommentsProps {
  comments: Comment[];
  budgetId: string;
  currentUser: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role?: string;
    permissions: string[];
  };
  onAddComment?: (comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt' | 'author'>) => void;
  onEditComment?: (commentId: string, content: string) => void;
  onDeleteComment?: (commentId: string) => void;
  onResolveComment?: (commentId: string) => void;
  onReplyToComment?: (commentId: string, reply: Omit<Comment, 'id' | 'createdAt' | 'updatedAt' | 'author' | 'replies'>) => void;
  onToggleNotification?: (commentId: string) => void;
  allowExternalComments?: boolean;
  allowInternalComments?: boolean;
  allowFileAttachments?: boolean;
  maxFileSize?: number;
  allowedFileTypes?: string[];
  className?: string;
}

const COMMENT_TYPE_CONFIG = {
  internal: {
    label: 'Interno',
    icon: Building,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Comentário interno da equipe'
  },
  external: {
    label: 'Cliente',
    icon: Users,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Comentário visível para o cliente'
  },
  note: {
    label: 'Nota',
    icon: Info,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Nota pessoal ou lembrete'
  }
};

const PRIORITY_CONFIG = {
  low: { label: 'Baixa', color: 'text-muted-foreground', bgColor: 'bg-card', icon: Info },
  medium: { label: 'Média', color: 'text-blue-500', bgColor: 'bg-blue-100', icon: Bell },
  high: { label: 'Alta', color: 'text-orange-500', bgColor: 'bg-orange-100', icon: AlertCircle },
  urgent: { label: 'Urgente', color: 'text-red-500', bgColor: 'bg-red-100', icon: Flag }
};

const VISIBILITY_CONFIG = {
  public: {
    label: 'Público',
    icon: Globe,
    color: 'text-green-600',
    description: 'Visível para todos'
  },
  private: {
    label: 'Privado',
    icon: Lock,
    color: 'text-foreground',
    description: 'Apenas para mim'
  },
  team: {
    label: 'Equipe',
    icon: Users,
    color: 'text-blue-600',
    description: 'Visível para a equipe'
  },
  client: {
    label: 'Cliente',
    icon: Building,
    color: 'text-green-600',
    description: 'Visível para o cliente'
  }
};

export function BudgetComments({
  comments,
  budgetId,
  currentUser,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onResolveComment,
  onReplyToComment,
  onToggleNotification,
  allowExternalComments = true,
  allowInternalComments = true,
  allowFileAttachments = true,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  allowedFileTypes = ['image/*', 'application/pdf', 'text/*'],
  className
}: BudgetCommentsProps) {
  const [activeTab, setActiveTab] = useState<'all' | 'internal' | 'external' | 'resolved'>('all');
  const [newComment, setNewComment] = useState({
    content: '',
    type: 'internal' as Comment['type'],
    visibility: 'team' as Comment['visibility'],
    priority: 'medium' as Comment['priority']
  });
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'priority'>('newest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter and sort comments
  const filteredComments = comments
    .filter(comment => {
      // Tab filtering
      if (activeTab === 'internal' && comment.type !== 'internal') return false;
      if (activeTab === 'external' && comment.type !== 'external') return false;
      if (activeTab === 'resolved' && comment.status !== 'resolved') return false;

      // Search filtering
      if (searchTerm && !comment.content.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Tag filtering
      if (selectedTags.length > 0 && !comment.tags?.some(tag => selectedTags.includes(tag))) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'priority':
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        default: // newest
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

  const handleAddComment = () => {
    if (!newComment.content.trim()) return;

    onAddComment?.({
      content: newComment.content,
      type: newComment.type,
      visibility: newComment.visibility,
      priority: newComment.priority,
      status: 'open',
      mentions: extractMentions(newComment.content),
      tags: extractTags(newComment.content),
      replies: []
    });

    setNewComment({
      content: '',
      type: 'internal',
      visibility: 'team',
      priority: 'medium'
    });
  };

  const handleReply = (commentId: string) => {
    if (!replyContent.trim()) return;

    onReplyToComment?.(commentId, {
      content: replyContent,
      type: 'internal',
      visibility: 'team',
      priority: 'medium',
      status: 'open',
      mentions: extractMentions(replyContent),
      tags: extractTags(replyContent)
    });

    setReplyContent('');
    setReplyingTo(null);
  };

  const handleEdit = (commentId: string) => {
    if (!editContent.trim()) return;

    onEditComment?.(commentId, editContent);
    setEditingComment(null);
    setEditContent('');
  };

  const extractMentions = (text: string): string[] => {
    const mentions = text.match(/@(\w+)/g);
    return mentions ? mentions.map(m => m.substring(1)) : [];
  };

  const extractTags = (text: string): string[] => {
    const tags = text.match(/#(\w+)/g);
    return tags ? tags.map(t => t.substring(1)) : [];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `há ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `há ${hours} hora${hours !== 1 ? 's' : ''}`;
    } else if (diffInHours < 48) {
      return 'ontem';
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEditComment = (comment: Comment) => {
    return comment.author.id === currentUser.id || currentUser.permissions.includes('edit_all_comments');
  };

  const canDeleteComment = (comment: Comment) => {
    return comment.author.id === currentUser.id || currentUser.permissions.includes('delete_all_comments');
  };

  const canResolveComment = (comment: Comment) => {
    return comment.status !== 'resolved' &&
           (comment.author.id === currentUser.id || currentUser.permissions.includes('resolve_comments'));
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Comentários e Discussões
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar comentários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Select value={sortBy} onValueChange={(value: typeof sortBy) => setSortBy(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Mais recentes</SelectItem>
                <SelectItem value="oldest">Mais antigos</SelectItem>
                <SelectItem value="priority">Por prioridade</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value: typeof activeTab) => setActiveTab(value)}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Todos ({comments.length})
            </TabsTrigger>
            {(allowInternalComments || currentUser.permissions.includes('view_internal_comments')) && (
              <TabsTrigger value="internal" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Internos ({comments.filter(c => c.type === 'internal').length})
              </TabsTrigger>
            )}
            {allowExternalComments && (
              <TabsTrigger value="external" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Cliente ({comments.filter(c => c.type === 'external').length})
              </TabsTrigger>
            )}
            <TabsTrigger value="resolved" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Resolvidos ({comments.filter(c => c.status === 'resolved').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      <CardContent>
        {/* New Comment Form */}
        {(allowInternalComments || allowExternalComments) && (
          <div className="mb-6">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex gap-3 mb-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={currentUser.avatar} />
                  <AvatarFallback>
                    {currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex gap-2 mb-2">
                    {(allowInternalComments && allowExternalComments) && (
                      <Select
                        value={newComment.type}
                        onValueChange={(value: Comment['type']) => setNewComment({ ...newComment, type: value })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {allowInternalComments && (
                            <SelectItem value="internal">Interno</SelectItem>
                          )}
                          {allowExternalComments && (
                            <SelectItem value="external">Cliente</SelectItem>
                          )}
                          <SelectItem value="note">Nota</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Select
                      value={newComment.visibility}
                      onValueChange={(value: Comment['visibility']) => setNewComment({ ...newComment, visibility: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Público</SelectItem>
                        <SelectItem value="team">Equipe</SelectItem>
                        <SelectItem value="client">Cliente</SelectItem>
                        <SelectItem value="private">Privado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={newComment.priority}
                      onValueChange={(value: Comment['priority']) => setNewComment({ ...newComment, priority: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baixa</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="urgent">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    value={newComment.content}
                    onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                    placeholder={newComment.type === 'external' ? "Escreva um comentário para o cliente..." : "Escreva um comentário interno..."}
                    className="min-h-[100px] resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        <AtSign className="h-4 w-4 mr-1" />
                        Mencionar
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Hash className="h-4 w-4 mr-1" />
                        Tag
                      </Button>
                      {allowFileAttachments && (
                        <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
                          <Paperclip className="h-4 w-4 mr-1" />
                          Anexo
                        </Button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={allowedFileTypes.join(',')}
                        className="hidden"
                      />
                    </div>
                    <Button onClick={handleAddComment} disabled={!newComment.content.trim()}>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar Comentário
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comments List */}
        {filteredComments.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhum comentário encontrado</h3>
            <p className="text-muted-foreground">
              {searchTerm || activeTab !== 'all'
                ? 'Nenhum comentário encontrado com os filtros selecionados'
                : 'Seja o primeiro a comentar sobre este orçamento'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredComments.map((comment) => {
              const typeConfig = COMMENT_TYPE_CONFIG[comment.type];
              const priorityConfig = PRIORITY_CONFIG[comment.priority];
              const visibilityConfig = VISIBILITY_CONFIG[comment.visibility];
              const TypeIcon = typeConfig.icon;
              const PriorityIcon = priorityConfig.icon;
              const VisibilityIcon = visibilityConfig.icon;

              return (
                <div key={comment.id} className={`rounded-lg border ${comment.status === 'resolved' ? 'bg-muted/20 opacity-75' : 'bg-background'}`}>
                  <div className="p-4">
                    {/* Comment Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={comment.author.avatar} />
                          <AvatarFallback className="text-xs">
                            {comment.author.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{comment.author.name}</span>
                            {comment.author.role && (
                              <Badge variant="outline" className="text-xs">
                                {comment.author.role}
                              </Badge>
                            )}
                            <Badge
                              variant="outline"
                              className={`text-xs ${typeConfig.color} ${typeConfig.bgColor}`}
                            >
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${priorityConfig.color} ${priorityConfig.bgColor}`}
                            >
                              <PriorityIcon className="h-3 w-3 mr-1" />
                              {priorityConfig.label}
                            </Badge>
                            {comment.visibility !== 'public' && (
                              <Badge
                                variant="outline"
                                className={`text-xs ${visibilityConfig.color}`}
                              >
                                <VisibilityIcon className="h-3 w-3 mr-1" />
                                {visibilityConfig.label}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span title={formatDateTime(comment.createdAt)}>
                              {formatDate(comment.createdAt)}
                            </span>
                            {comment.updatedAt !== comment.createdAt && (
                              <span>(editado)</span>
                            )}
                            {comment.status === 'resolved' && comment.resolvedAt && (
                              <span className="text-green-600">
                                • Resolvido por {comment.resolvedBy?.name} em {formatDate(comment.resolvedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEditComment(comment) && (
                            <DropdownMenuItem onClick={() => {
                              setEditingComment(comment.id);
                              setEditContent(comment.content);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {canResolveComment(comment) && (
                            <DropdownMenuItem onClick={() => onResolveComment?.(comment.id)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Marcar como Resolvido
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem>
                            <Bell className="h-4 w-4 mr-2" />
                            {comment.status === 'open' ? 'Ativar Notificações' : 'Silenciar'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {canDeleteComment(comment) && (
                            <DropdownMenuItem
                              onClick={() => onDeleteComment?.(comment.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Comment Content */}
                    {editingComment === comment.id ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="min-h-[100px] resize-none"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleEdit(comment.id)}>
                            Salvar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingComment(null)}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        <div className="whitespace-pre-wrap">{comment.content}</div>

                        {/* Mentions */}
                        {comment.mentions && comment.mentions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {comment.mentions.map((mention) => (
                              <Badge key={mention} variant="outline" className="text-xs">
                                <AtSign className="h-3 w-3 mr-1" />
                                {mention}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Tags */}
                        {comment.tags && comment.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {comment.tags.map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                <Hash className="h-3 w-3 mr-1" />
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Attachments */}
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {comment.attachments.map((attachment) => (
                              <div key={attachment.id} className="flex items-center justify-between bg-muted/30 rounded p-2">
                                <div className="flex items-center gap-2">
                                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{attachment.name}</span>
                                  <span className="text-xs text-muted-foreground">({attachment.size})</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Reply Section */}
                    {comment.status === 'open' && (
                      <div className="mt-4 border-t pt-3">
                        {replyingTo === comment.id ? (
                          <div className="space-y-3">
                            <div className="flex gap-3">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={currentUser.avatar} />
                                <AvatarFallback className="text-xs">
                                  {currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <Textarea
                                  value={replyContent}
                                  onChange={(e) => setReplyContent(e.target.value)}
                                  placeholder="Escreva uma resposta..."
                                  className="min-h-[60px] resize-none"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 ml-9">
                              <Button size="sm" onClick={() => handleReply(comment.id)}>
                                Responder
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setReplyingTo(null)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReplyingTo(comment.id)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Reply className="h-4 w-4 mr-1" />
                            Responder
                          </Button>
                        )}

                        {/* Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="mt-3 space-y-3">
                            {comment.replies.map((reply) => (
                              <div key={reply.id} className="flex gap-3 bg-muted/20 rounded p-3">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={reply.author.avatar} />
                                  <AvatarFallback className="text-xs">
                                    {reply.author.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium">{reply.author.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {formatDate(reply.createdAt)}
                                    </span>
                                  </div>
                                  <div className="text-sm whitespace-pre-wrap">{reply.content}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}